import {TimelineLite} from "gsap";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {DjPsychoSymbol} from "../../../game/reels/DjPsychoSymbol";
import {DjPsycho} from "../../../game/DjPsycho";

export interface ISimpleReelModelDelegate {
    createSymbol(symbolName: string, reelID: number, soundId: number): DjPsychoSymbol;

    removeSymbol(symbolData: DjPsychoSymbol): void;
}

export enum ISimpleReelModelState {
    IDLE = "IDLE",
    DISAPPEAR = "DISAPPEAR",
    APPEAR = "APPEAR"
}

export interface ISimpleReelModelEventData {
    reel: number;
    state: ISimpleReelModelState;
}

export class SimpleReelModel {
    public stepSize: number = 132;
    public stepDuration = 0.1;
    public readonly id: number;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events: APIEventSystem | undefined;
    private _delegate: ISimpleReelModelDelegate;

    constructor(id: number, delegate: ISimpleReelModelDelegate, triggerEvents: boolean = false) {
        this.id = id;
        this._delegate = delegate;
        if (triggerEvents) {
            this.events = DjPsycho.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data: ISimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    private _symbols: DjPsychoSymbol[] = [];

    get symbols(): DjPsychoSymbol[] {
        return this._symbols;
    }

    private _state: ISimpleReelModelState = ISimpleReelModelState.IDLE;

    get state(): ISimpleReelModelState {
        return this._state;
    }

    set state(value: ISimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <ISimpleReelModelEventData>{reel: this.id, state: this._state});
        }
    }

    public init(symbols: string[]): void {
        this.clearSymbols(this._symbols);

        if (symbols.length !== DjPsycho.rowsNum) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        for (let i: number = 0; i < symbols.length; i++) {
            const soundId: number = (this.id + 1),
                symbol: DjPsychoSymbol = this.createNewSymbol(symbols[i], (i === 0 ? soundId : (soundId + (symbols.length * i)))),
                basePos: number = this.stepSize * i;

            symbol.y = basePos + (i * DjPsycho.padding);
            this._symbols.push(symbol);
        }
    }

    public stop(stopSymbolNames: string[]): void {
        stopSymbolNames.forEach((name: string, i: number) => {
            this._symbols[i].updateTexture(name);
        });
    }

    public disappearSymbolAt(pos: number, index: number): TimelineLite {
        return this._symbols[pos].disappear().eventCallback("onComplete", () => {
            DjPsycho.winView.anticipateAppearAnimation(this.id, pos, index);
        });
    }

    public appearSymbolAt(pos: number): TimelineLite {
        return this._symbols[pos].appear(this.id, pos);
    }

    private onReelStateChange(data: ISimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    private createNewSymbol(name: string, soundId: number): DjPsychoSymbol {
        return this._delegate.createSymbol(name, this.id, soundId);
    }

    public clearSymbols(symbols: DjPsychoSymbol[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this._delegate.removeSymbol(symbol);
        }
    }
}